import { CookiePreferencesModal } from '@coinbase/cookie-banner';
import defaultTheme from '@coinbase/cookie-banner/src/utils/defaultTheme';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { SelectOption, type SelectOptionProps } from '@cbhq/cds-web/controls';
import React, { useMemo } from 'react';
import { HStack } from '@cbhq/cds-web/layout';
import { TextCaption } from '@cbhq/cds-web/typography';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { useChainSwitch } from '@/components/ChainSwitch/ChainSwitchContext';
import { ChainSwitchModal } from '@/components/ChainSwitch/ChainSwitchModal';
import { TestnetToggleButton } from '@/components/ChainSwitch/TestnetToggleButton';
import { TestnetModeModal } from '@/components/ChainSwitch/TestnetModeModal';
import { useIsMobileView } from '@/hooks/useIsMobileView';
import type { ColorScheme } from '@cbhq/cds-common/core/theme';

type Props = {
  accessibilityLabelThemeButton: string;
  theme: ColorScheme;
  toggleTheme: () => void;
};

function NavbarSettingsDropdown({ accessibilityLabelThemeButton, theme, toggleTheme }: Props) {
  const [cookiePreferencesModalVisible, setCookiePreferencesModalVisible] = React.useState(false);
  const showCookiePreferencesModal = React.useCallback(
    () => setCookiePreferencesModalVisible(true),
    [],
  );
  const hideCookiePreferencesModal = React.useCallback(
    () => setCookiePreferencesModalVisible(false),
    [],
  );

  const { testnetModeModalVisible, hideTestnetModeModal, testnetSupported } = useChainSwitch();

  const isMobile = useIsMobileView();

  const content = useMemo(() => {
    const options: SelectOptionProps[] = [
      {
        value: 'toggle-theme',
        accessory: (
          <Icon
            name={theme === 'dark' ? 'moon' : 'light'}
            size="m"
            accessibilityLabel={accessibilityLabelThemeButton}
            color="fg"
          />
        ),
        title: 'Toggle theme',
        onClick: toggleTheme,
      },
      {
        value: 'cookie-preferences',
        title: 'Cookie preferences',
        onClick: showCookiePreferencesModal,
      },
      // Add the testnet toggle option
      ...(testnetSupported
        ? [
            {
              value: 'toggle-testnet',
              title: <TestnetToggleButton />,
              testID: 'navbar-settings-testnet-toggle',
            },
          ]
        : []),
    ];

    return (
      <>
        <HStack paddingX={2} paddingY={2} width="100%">
          <TextCaption as="h2" color="fgMuted">
            Settings
          </TextCaption>
        </HStack>
        {options.map((option) => (
          <SelectOption
            key={option.value}
            value={option.value}
            title={option.title}
            accessory={option.accessory}
            onClick={option?.onClick}
            testID={option?.testID}
          />
        ))}
      </>
    );
  }, [
    theme,
    accessibilityLabelThemeButton,
    toggleTheme,
    showCookiePreferencesModal,
    testnetSupported,
  ]);

  // Cookie modal theming overrides
  const cookiePreferencesModalTheme = useMemo(() => {
    return {
      ...defaultTheme,
      border: {
        ...defaultTheme.border,
        borderRadius: '8px',
      },
      colors: {
        ...defaultTheme.colors,
        overlay: theme === 'light' ? 'rgba(50, 53, 61, 0.33)' : 'rgba(10, 11, 13, 0.5)',
      },
    };
  }, [theme]);

  return (
    <>
      {isMobile ? (
        <Dropdown content={content} enableMobileModal>
          <IconButton as="button" name="more" testID="navbar-settings-button" />
        </Dropdown>
      ) : (
        // Mobile doesn't support showOverlay
        <Dropdown content={content} enableMobileModal showOverlay>
          <IconButton as="button" name="more" testID="navbar-settings-button" />
        </Dropdown>
      )}
      <CookiePreferencesModal
        isOpen={cookiePreferencesModalVisible}
        onClose={hideCookiePreferencesModal}
        theme={cookiePreferencesModalTheme}
      />
      <ChainSwitchModal />
      <TestnetModeModal visible={testnetModeModalVisible} onClose={hideTestnetModeModal} />
    </>
  );
}

export { NavbarSettingsDropdown };
